import React, { useEffect, useRef } from 'react';
import { useTheme } from '../ThemeContext';
import { createNoise3D } from 'simplex-noise';

const SwirlBackground = () => {
    const containerRef = useRef(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    useEffect(() => {
        if (!containerRef.current) return;

        // Constants
        const isMobile = window.innerWidth < 768;
        const particleCount = isMobile ? 150 : 400;
        const particlePropCount = 9;
        const particlePropsLength = particleCount * particlePropCount;
        const rangeY = 100;
        const baseTTL = 50;
        const rangeTTL = 150;
        const baseSpeed = 0.1;
        const rangeSpeed = isMobile ? 1.2 : 2;
        const baseRadius = 1;
        const rangeRadius = isMobile ? 3 : 4;
        const baseHue = isDark ? 220 : 200;
        const rangeHue = 100;
        const noiseSteps = 8;
        const xOff = 0.00125;
        const yOff = 0.00125;
        const zOff = 0.0005;
        const backgroundColor = isDark ? 'hsla(0,0%,0%,1)' : 'hsla(210,30%,95%,1)';

        const { PI, cos, sin, abs, random } = Math;
        const TAU = 2 * PI;
        const rand = (n) => n * random();
        const randRange = (n) => n - rand(2 * n);
        const fadeInOut = (t, m) => {
            const hm = 0.5 * m;
            return abs((t + hm) % m - hm) / hm;
        };
        const lerp = (a, b, t) => a + (b - a) * t;

        let canvas;
        let ctx;
        let center = [];
        let tick = 0;
        const noise3D = createNoise3D();
        let particleProps;
        let animationId;

        function initParticle(i) {
            const x = rand(canvas.a.width);
            const y = center[1] + randRange(rangeY);
            const vx = 0;
            const vy = 0;
            const life = 0;
            const ttl = baseTTL + rand(rangeTTL);
            const speed = baseSpeed + rand(rangeSpeed);
            const radius = baseRadius + rand(rangeRadius);
            const hue = baseHue + rand(rangeHue);

            particleProps.set([x, y, vx, vy, life, ttl, speed, radius, hue], i);
        }

        function initParticles() {
            tick = 0;
            particleProps = new Float32Array(particlePropsLength);

            for (let i = 0; i < particlePropsLength; i += particlePropCount) {
                initParticle(i);
            }
        }

        function drawParticle(x, y, x2, y2, life, ttl, radius, hue) {
            ctx.a.save();
            ctx.a.lineCap = 'round';
            // Make particles larger in light mode for better visibility
            ctx.a.lineWidth = isDark ? radius : radius * 1.5;
            // In dark mode: bright glowing colors, in light mode: vibrant saturated colors
            const lightness = isDark ? 60 : 50;
            const saturation = 100;
            ctx.a.strokeStyle = `hsla(${hue},${saturation}%,${lightness}%,${fadeInOut(life, ttl)})`;
            ctx.a.beginPath();
            ctx.a.moveTo(x, y);
            ctx.a.lineTo(x2, y2);
            ctx.a.stroke();
            ctx.a.closePath();
            ctx.a.restore();
        }

        function checkBounds(x, y) {
            return x > canvas.a.width || x < 0 || y > canvas.a.height || y < 0;
        }

        function updateParticle(i) {
            const i2 = 1 + i, i3 = 2 + i, i4 = 3 + i, i5 = 4 + i, i6 = 5 + i, i7 = 6 + i, i8 = 7 + i, i9 = 8 + i;

            const x = particleProps[i];
            const y = particleProps[i2];
            const n = noise3D(x * xOff, y * yOff, tick * zOff) * noiseSteps * TAU;
            const vx = lerp(particleProps[i3], cos(n), 0.5);
            const vy = lerp(particleProps[i4], sin(n), 0.5);
            let life = particleProps[i5];
            const ttl = particleProps[i6];
            const speed = particleProps[i7];
            const x2 = x + vx * speed;
            const y2 = y + vy * speed;
            const radius = particleProps[i8];
            const hue = particleProps[i9];

            drawParticle(x, y, x2, y2, life, ttl, radius, hue);

            life++;

            particleProps[i] = x2;
            particleProps[i2] = y2;
            particleProps[i3] = vx;
            particleProps[i4] = vy;
            particleProps[i5] = life;

            if (checkBounds(x, y) || life > ttl) {
                initParticle(i);
            }
        }

        function drawParticles() {
            for (let i = 0; i < particlePropsLength; i += particlePropCount) {
                updateParticle(i);
            }
        }

        function renderGlow() {
            const isMobile = window.innerWidth < 768;
            if (isDark) {
                // Dark mode: tight glow on particles only — avoid flooding the
                // whole viewport with bluish haze, so the background stays pitch black.
                ctx.b.save();
                ctx.b.filter = isMobile ? 'blur(2px) brightness(110%)' : 'blur(4px) brightness(130%)';
                ctx.b.globalCompositeOperation = 'lighter';
                ctx.b.drawImage(canvas.a, 0, 0);
                ctx.b.restore();
            } else {
                // Light mode: subtle glow with color preservation
                ctx.b.save();
                ctx.b.filter = isMobile ? 'blur(2px)' : 'blur(4px)';
                ctx.b.globalCompositeOperation = 'source-over';
                ctx.b.drawImage(canvas.a, 0, 0);
                ctx.b.restore();
            }
        }

        function renderToScreen() {
            ctx.b.save();
            ctx.b.globalCompositeOperation = 'source-over';
            ctx.b.drawImage(canvas.a, 0, 0);
            ctx.b.restore();
        }

        function draw() {
            tick++;

            ctx.a.clearRect(0, 0, canvas.a.width, canvas.a.height);
            ctx.b.fillStyle = backgroundColor;
            ctx.b.fillRect(0, 0, canvas.a.width, canvas.a.height);

            drawParticles();
            renderGlow();
            renderToScreen();

            animationId = window.requestAnimationFrame(draw);
        }

        function resize() {
            const { innerWidth, innerHeight } = window;

            canvas.a.width = innerWidth;
            canvas.a.height = innerHeight;

            ctx.a.drawImage(canvas.b, 0, 0);

            canvas.b.width = innerWidth;
            canvas.b.height = innerHeight;

            ctx.b.drawImage(canvas.a, 0, 0);

            center[0] = 0.5 * canvas.a.width;
            center[1] = 0.5 * canvas.a.height;
        }

        function createCanvas() {
            const container = containerRef.current;

            canvas = {
                a: document.createElement('canvas'),
                b: document.createElement('canvas')
            };

            canvas.b.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        pointer-events: none;
      `;

            container.appendChild(canvas.b);

            ctx = {
                a: canvas.a.getContext('2d'),
                b: canvas.b.getContext('2d')
            };

            center = [];
        }

        function setup() {
            createCanvas();
            resize();
            initParticles();
            draw();
        }

        setup();
        window.addEventListener('resize', resize);
        const container = containerRef.current;

        return () => {
            window.removeEventListener('resize', resize);
            if (animationId) {
                window.cancelAnimationFrame(animationId);
            }
            if (container && canvas?.b) {
                container.removeChild(canvas.b);
            }
        };
    }, [isDark]);

    return <div ref={containerRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />;
};

export default SwirlBackground;
